(function(angular, _) {
    angular.module('wtApp.dataService')
        .factory('WtReportFiltersFactory', ['$injector', function($injector) {
            const $log = $injector.get('$log');
            const $translate = $injector.get('$translate');
            const segmentService = $injector.get('segmentService');
            const Monads = $injector.get('Monads');
            const WtObservable = $injector.get('WtObservable');

            const SEGMENTS_DIMENSION_TITLE = $translate.instant('SEGMENTS_TITLE');
            const SEGMENTS_DIMENSION_GROUP = 'VISITORS';
            const SEGMENTS_DROPDOWN_DEFAUL = $translate.instant('SEGMENTS_DROPDOWN_DEFAULT_VALUE');

            /**
             * @ngdoc factory
             * @name WtReportFiltersFactory
             * @description Implements the report quick filters handling
             */
            return class WtReportFiltersFactory {
                constructor(reportFiltersList = []) {
                    /**
                     * Holds the original filters
                     * @type {Array}
                     */
                    this.pristineData_ = _.cloneDeep(reportFiltersList);

                    /**
                     * Holds the working copy of the quickFilters
                     * @type {Array}
                     */
                    this.data_ = [];

                    /**
                     * The observer object that can be used to push changes into the observable
                     * @example
                     *  - Notify the listeners about a new value: this.changesEmitter_.next('value');
                     *  - Notify the listeners about an error: this.changesEmitter_.error(new Error('Something went wrong'))
                     *  - Notify the listeners about the completion: this.changesEmitter_.complete()
                     * @type {null|Object}
                     * @private
                     */
                    this.changesEmitter_ = null;

                    /**
                     * The observable object that can be subscribed to listen to the changes on the report filters instance
                     * @example
                     *  const subscription = filters.changes.subscribe((action) => {
                     *      const type = action.type;
                     *      const payload = action.payload;
                     *      // React to the report filters change
                     *  });
                     *
                     *  // Cleanup method should be invoked when the listener is destroyed or does not need the updates
                     *  subscription.unsubscribe();
                     * @type {WtObservable}
                     */
                    this.changes = new WtObservable((observer) => {
                        this.changesEmitter_ = observer;
                    });

                    /**
                     * Enhances the report.filters changes observable by adding a filter to be able to subscribe to specific action(s)
                     * @param {string|Array} actionType One or more action types to subscribe to
                     * @example
                     *  const subscription = filters.changesOfType('PROPERTY_CHANGE').subscribe((action) => {
                     *      const type = action.type;
                     *      const payload = action.payload;
                     *      // React to the report change
                     *  });
                     *
                     *  // Cleanup method should be invoked when the listener is destroyed or does not need the updates
                     *  subscription.unsubscribe();
                     * @returns {WtObservable}
                     */
                    this.changesOfType = function(actionType) {
                        return this.changes
                            .filter((action) => _.includes(_.isArray(actionType) ? actionType : [actionType], _.get(action, 'type')));
                    };

                    this.init_();
                }

                /**
                 * @ngdoc property
                 * @name WtReportFiltersFactory#ACTIONS
                 * @description Hash map of actions dispatched
                 * @returns {Object}
                 */
                get ACTIONS() {
                    return {
                        /**
                         * Triggered on filters change
                         * @type {string}
                         */
                        QUICK_FILTERS_CHANGED: 'QUICK_FILTERS_CHANGED'
                    };
                }

                /**
                 * @ngdoc property
                 * @name WtReportFiltersFactory#pristineConfig
                 * @description Exposes the pristine filters list to the outer world
                 * @returns {Array}
                 */
                get pristineConfig() {
                    return this.pristineData_;
                }

                /**
                 * @ngdoc property
                 * @name WtReportFiltersFactory#config
                 * @description Exposes the filters list to the outer world
                 * @returns {Array}
                 */
                get config() {
                    return this.data_;
                }

                /**
                 * @ngdoc property
                 * @name WtReportFiltersFactory#quickFiltersQuotaExceeded
                 * @description Determines if the limit of dashboard quick filters has been reached
                 * @returns {boolean}
                 */
                get quickFiltersQuotaExceeded() {
                    return _.size(this.config) >= 8;
                }

                /**
                 * @ngdoc property
                 * @name WtReportFiltersFactory#isPristine
                 * @description Determines whether the dashboard quick filters have been changed.
                 * @returns {boolean}
                 */
                get isPristine() {
                    return _.isEqual(this.pristineConfig, this.config);
                }

                /**
                 * @ngdoc method
                 * @name WtReportFiltersFactory#dispatchAction
                 * @description Emits an event in the report filters changes observable.
                 * @param {string} type The event name
                 * @param {Object=} payload The payload of the event
                 * @returns {WtReportFiltersFactory}
                 */
                dispatchAction(type, payload = {}) {
                    if (_.hasIn(this.ACTIONS, type)) {
                        this.changesEmitter_.next({type, payload});
                    }

                    return this;
                }

                init_() {
                    this.data_ = [];

                    _.forEach(this.pristineConfig, (filter) => {
                        const dimension = this.getMonad_(_.get(filter, 'arrangeType'));

                        this.addQuickFilter(
                            this.createQuickFilter(filter, dimension)
                        );
                    });

                    return this;
                }

                createQuickFilter(filter, dimension) {
                    const type = 'REPORT_FILTER';
                    const title = _.get(dimension, 'title', '');

                    return _.create({title, type}, filter);
                }

                addQuickFilter(filter) {
                    const arrangeType = _.get(filter, 'arrangeType', '');
                    const filterClone = _.cloneDeep(filter);

                    if (_.find(this.config, {arrangeType})) {
                        $log.warn('Attempted to add a dashboard quick filter with an arrange type that already exists in the list');

                        return 0;
                    }

                    const previousValue = _.cloneDeep(this.config);

                    this.data_.push(filterClone);

                    this.data_ = this.getSortedFilters();

                    const currentValue = _.cloneDeep(this.config);

                    this.dispatchAction(this.ACTIONS.QUICK_FILTERS_CHANGED, {currentValue, previousValue});

                    return 1;
                }

                getSortedFilters() {
                    return _.orderBy(this.data_, ['title'], ['asc']);
                }

                /**
                 * @ngdoc method
                 * @name WtReportFiltersFactory#getSegmentDimension_
                 * @description Builds and returns the segments as dimension
                 * @returns {Object}
                 * @private
                 */
                getSegmentDimension_() {
                    return segmentService.getSegmentAsDimensionTemplate(
                        SEGMENTS_DIMENSION_TITLE,
                        SEGMENTS_DIMENSION_GROUP,
                        SEGMENTS_DROPDOWN_DEFAUL
                    );
                }

                /**
                 * @ngdoc method
                 * @name WtReportFiltersFactory#getMonad_
                 * @description Retrieves monad configuration based on arrangeType
                 * @returns {Object}
                 * @private
                 */
                getMonad_(arrangeType) {
                    if (arrangeType === segmentService.getFakeSegmentArrangeType()) {
                        return this.getSegmentDimension_();
                    }

                    return Monads.monad(arrangeType);
                }

                /**
                 * @ngdoc method
                 * @name WtReportFiltersFactory#hasActiveFilters_
                 * @description Determines whether the current list of quick filters contains any filter rules that might
                 * change the current result set of a report.
                 * @returns {boolean}
                 * @private
                 */
                hasActiveFilters_() {
                    const hasFilterValue = (quickFilter) => {
                        const filterValue = _.get(quickFilter, 'value1', '').trim();

                        return !_.includes(['', '*'], filterValue);
                    };

                    return _.reduce(this.config, (hasActive, quickFilter) => (hasActive || hasFilterValue(quickFilter)), false);
                }

                resetFilters() {
                    this.init_();

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtReportFiltersFactory#resetValues
                 * @description Will empty the value1 keys for a list of given quick filters.
                 * @returns {WtReportFiltersFactory}
                 */
                resetValues() {
                    if (!this.hasActiveFilters_()) {
                        return this;
                    }

                    const previousValue = _.cloneDeep(this.config);

                    _.forEach(this.config, (quickFilter) => {
                        _.set(quickFilter, 'value1', '');
                    });

                    const currentValue = _.cloneDeep(this.config);

                    this.dispatchAction(this.ACTIONS.QUICK_FILTERS_CHANGED, {currentValue, previousValue});

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtReportFiltersFactory#deleteQuickFilter
                 * @description Drops the quick filter from the list
                 * @param {Object|string} quickFilter The quick filter object or its arrangeType
                 * @returns {Array} Returns the new array of removed filters
                 */
                deleteQuickFilter(quickFilter) {
                    const arrangeType = _.isString(quickFilter) ? quickFilter : _.get(quickFilter, 'arrangeType');
                    const previousValue = _.cloneDeep(this.config);
                    const removedFiltersList = _.remove(this.config, {arrangeType});
                    const currentValue = _.cloneDeep(this.config);

                    if (_.size(removedFiltersList)) {
                        this.dispatchAction(this.ACTIONS.QUICK_FILTERS_CHANGED, {currentValue, previousValue});
                    }

                    return removedFiltersList;
                }

                destroy() {
                    if (this.changesEmitter_) {
                        this.changesEmitter_.complete();
                    }
                }
            };
        }]);
})(window.angular, window._);
